import React, { useEffect, useState } from "react";
import { createIcons, icons } from "lucide";
import { initFlowbite } from "flowbite";

export default function Navbar() {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Initialize theme from localStorage or default to system preference
    const savedTheme = localStorage.getItem("theme");
    return savedTheme
      ? savedTheme === "dark"
      : document.documentElement.classList.contains("dark");
  });
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    // Initialize sidebar state from localStorage or default to open
    const savedSidebar = localStorage.getItem("sidebar");
    return savedSidebar ? savedSidebar === "open" : true;
  });

  useEffect(() => {
    // Initialize Lucide icons and Flowbite
    createIcons({ icons });
    initFlowbite();

    // Apply saved theme on mount
    const html = document.querySelector("html");
    const body = document.body;
    if (isDarkMode) {
      html.classList.add("dark");
      body.setAttribute("data-layout-mode", "dark");
    } else {
      html.classList.remove("dark");
      body.setAttribute("data-layout-mode", "light");
    }

    // Apply saved sidebar state on mount
    const sidebar = document.querySelector(".main-sidebar");
    if (sidebar) {
      if (isSidebarOpen) {
        sidebar.classList.remove("hidden");
      } else {
        sidebar.classList.add("hidden");
      }
    }

    // Theme toggle handler
    const toggletheme = document.querySelector("#toggle-theme");
    const lightBtn = toggletheme?.querySelector(".light");
    const darkBtn = toggletheme?.querySelector(".dark");

    const handleThemeToggle = () => {
      html.classList.toggle("dark");
      const isDark = html.classList.contains("dark");
      body.setAttribute("data-layout-mode", isDark ? "dark" : "light");
      localStorage.setItem("theme", isDark ? "dark" : "light");
      if (!isDark) {
        darkBtn?.classList.add("hidden");
        lightBtn?.classList.remove("hidden");
      } else {
        darkBtn?.classList.remove("hidden");
        lightBtn?.classList.add("hidden");
      }
      setIsDarkMode(isDark);
    };

    // Sidebar toggle handler
    const toggleMenuButton = document.querySelector("#toggle-menu-hide");
    const handleMenuToggle = () => {
      if (sidebar) {
        sidebar.classList.toggle("hidden");
        const isOpen = !sidebar.classList.contains("hidden");
        localStorage.setItem("sidebar", isOpen ? "open" : "closed");
        setIsSidebarOpen(isOpen);
      }
    };

    // Open sidebar and relevant menus/submenus for active page
    const activePath = window.location.pathname;
    const navLinks = document.querySelectorAll("ul.navbar-nav a.nav-link");

    navLinks.forEach((link) => {
      const href = link.getAttribute("href");
      if (href && activePath.includes(href)) {
        // Add active class to the link
        link.classList.add("active");

        // Open the sidebar
        if (sidebar) {
          sidebar.classList.remove("hidden");
          localStorage.setItem("sidebar", "open");
          setIsSidebarOpen(true);
        }

        // Open parent menu and submenu
        let parentCollapse = link.closest('[data-collapse-toggle]');
        while (parentCollapse) {
          const targetId = parentCollapse.getAttribute("data-collapse-toggle");
          const target = targetId ? document.getElementById(targetId) : null;
          if (target) {
            target.classList.remove("hidden");
            const icon = parentCollapse.querySelector("i.icofont-thin-down");
            if (icon) {
              icon.classList.add("rotate-180");
            }
          }
          // Move up to check for nested parent (submenu)
          parentCollapse = parentCollapse.parentElement.closest(
            '[data-collapse-toggle]'
          );
        }
      }
    });

    // Attach event listeners
    if (toggletheme) {
      toggletheme.addEventListener("click", handleThemeToggle);
    }
    if (toggleMenuButton) {
      toggleMenuButton.addEventListener("click", handleMenuToggle);
    }

    // Cleanup event listeners
    return () => {
      if (toggletheme) {
        toggletheme.removeEventListener("click", handleThemeToggle);
      }
      if (toggleMenuButton) {
        toggleMenuButton.removeEventListener("click", handleMenuToggle);
      }
    };
  }, [isDarkMode, isSidebarOpen]);

  return (
    <nav
      id="topbar"
      className="topbar border-b dark:border-slate-700/40 fixed inset-x-0 duration-300 block print:hidden z-50"
    >
      <div className="mx-0 flex max-w-full flex-wrap items-center lg:mx-auto relative top-[50%] start-[50%] transform -translate-x-1/2 -translate-y-1/2">
        <div className="ltr:mx-2 rtl:mx-2">
          <button
            id="toggle-menu-hide"
            className="button-menu-mobile flex rounded-full md:me-0 relative"
          >
            <i data-lucide="menu" className="top-icon w-5 h-5" />
          </button>
        </div>
        <div className="flex items-center md:w-[40%] lg:w-[30%] xl:w-[20%]">
          <div className="relative ltr:mx-2 rtl:mx-2 self-center">
            <button className="px-2 py-1 bg-primary-500/10 border border-transparent collapse:bg-green-100 text-primary text-sm rounded hover:bg-blue-600 hover:text-white">
              <i className="ti ti-plus me-1" /> New Task
            </button>
          </div>
        </div>
        <div className="order-1 ltr:ms-auto rtl:ms-0 rtl:me-auto flex items-center md:order-2">
          <div className="ltr:me-2 ltr:md:me-4 rtl:me-0 rtl:ms-2 rtl:lg:me-0 rtl:md:ms-4 dropdown relative">
            <button
              type="button"
              className="dropdown-toggle flex rounded-full md:me-0"
              aria-expanded="false"
              data-fc-autoclose="both"
              data-fc-type="dropdown"
            >
              <span data-lucide="search" className="top-icon w-5 h-5" />
            </button>
            <div
              className="left-auto right-0 z-50 my-1 hidden min-w-[300px] list-none divide-y divide-gray-100 rounded-md border-slate-700 md:border-white text-base shadow dark:divide-gray-600 bg-white dark:bg-slate-800"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <i className="ti ti-search text-gray-400 z-10" />
                </div>
                <input
                  type="text"
                  id="email-adress-icon"
                  className="block w-full rounded-lg border border-slate-200 dark:border-slate-700/60 bg-slate-200/10 p-1.5 pl-10 text-slate-600 dark:text-slate-400 outline-none focus:border-slate-300 focus:ring-slate-300 dark:bg-slate-800/20 sm:text-sm"
                  placeholder="Search..."
                />
              </div>
            </div>
          </div>
          <div className="ltr:me-2 ltr:md:me-4 rtl:me-0 rtl:ms-2 rtl:lg:me-0 rtl:md:ms-4">
            <button
              id="toggle-theme"
              className="flex rounded-full md:me-0 relative"
            >
              <span
                data-lucide="moon"
                className={`top-icon w-5 h-5 light ${isDarkMode ? "hidden" : ""}`}
              />
              <span
                data-lucide="sun"
                className={`top-icon w-5 h-5 dark ${isDarkMode ? "" : "hidden"}`}
              />
            </button>
          </div>
          <div className="ltr:me-2 ltr:lg:me-4 rtl:me-0 rtl:ms-2 rtl:lg:me-0 rtl:md:ms-4 dropdown relative">
            <button
              type="button"
              className="dropdown-toggle flex rounded-full md:me-0"
              id="Notifications"
              aria-expanded="false"
              data-fc-autoclose="both"
              data-fc-type="dropdown"
            >
              <span data-lucide="bell" className="top-icon w-5 h-5" />
            </button>
            <div
              className="left-auto right-0 z-50 my-1 hidden w-64 list-none divide-y h-52 divide-gray-100 rounded border border-slate-700/10 text-base shadow dark:divide-gray-600 bg-white dark:bg-slate-800"
              id="navNotifications"
              data-simplebar=""
            >
              <ul className="py-1" aria-labelledby="Notifications">
                <li className="py-2 px-4">
                  <a href="javascript:void(0);" className="dropdown-item">
                    <div className="flex">
                      <div className="h-8 w-8 rounded-full bg-primary-500/20 inline-flex me-3">
                        <span
                          data-lucide="shopping-cart"
                          className="w-4 h-4 inline-block text-primary-500 dark:text-primary-400 self-center mx-auto"
                        />
                      </div>
                      <div className="flex-grow flex-1 ms-0.5 overflow-hidden">
                        <p className="text-sm font-medium text-gray-900 truncate dark:text-gray-300">
                          Karen Robinson
                        </p>
                        <p className="text-gray-500 mb-0 text-xs truncate dark:text-gray-400">
                          Hey ! i'm available here
                        </p>
                      </div>
                    </div>
                  </a>
                </li>
                <li className="py-2 px-4">
                  <a href="javascript:void(0);" className="dropdown-item">
                    <div className="flex">
                      <img
                        className="object-cover rounded-full h-8 w-8 shrink-0 me-3"
                        src="/images/users/avatar-3.png"
                        alt="logo"
                      />
                      <div className="flex-grow flex-1 ms-0.5 overflow-hidden">
                        <p className="text-sm font-medium text-gray-900 truncate dark:text-gray-300">
                          Your order is placed
                        </p>
                        <p className="text-gray-500 mb-0 text-xs truncate dark:text-gray-400">
                          Dummy text of the printing and industry.
                        </p>
                      </div>
                    </div>
                  </a>
                </li>
                <li className="py-2 px-4">
                  <a href="javascript:void(0);" className="dropdown-item">
                    <div className="flex">
                      <div className="h-8 w-8 rounded-full bg-primary-500/20 inline-flex me-3">
                        <span
                          data-lucide="user"
                          className="w-4 h-4 inline-block text-primary-500 dark:text-primary-400 self-center mx-auto"
                        />
                      </div>
                      <div className="flex-grow flex-1 ms-0.5 overflow-hidden">
                        <p className="text-sm font-medium text-gray-900 truncate dark:text-gray-300">
                          Robert McCray
                        </p>
                        <p className="text-gray-500 mb-0 text-xs truncate dark:text-gray-400">
                          Good Morning!
                        </p>
                      </div>
                    </div>
                  </a>
                </li>
                <li className="py-2 px-4">
                  <a href="javascript:void(0);" className="dropdown-item">
                    <div className="flex">
                      <img
                        className="object-cover rounded-full h-8 w-8 shrink-0 me-3"
                        src="/images/users/avatar-9.png"
                        alt="logo"
                      />
                      <div className="flex-grow flex-1 ms-0.5 overflow-hidden">
                        <p className="text-sm font-medium text-gray-900 truncate dark:text-gray-300">
                          Meeting with designers
                        </p>
                        <p className="text-gray-500 mb-0 text-xs truncate dark:text-gray-400">
                          It is a long established fact that a reader.
                        </p>
                      </div>
                    </div>
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="me-2 dropdown relative">
            <button
              type="button"
              className="dropdown-toggle flex items-center rounded-full text-sm focus:bg-none focus:ring-0 dark:focus:ring-0 md:me-0"
              id="user-profile"
              aria-expanded="false"
              data-fc-autoclose="both"
              data-fc-type="dropdown"
            >
              <img
                className="h-8 w-8 rounded-full"
                src="/images/users/avatar-1.png"
                alt="user photo"
              />
              <span className="ltr:ms-2 rtl:ms-0 rtl:me-2 hidden text-left xl:block">
                <span className="block font-medium text-slate-600 dark:text-gray-300">
                  Maria Gibson
                </span>
                <span className="-mt-0.5 block text-xs text-slate-500 dark:text-gray-400">
                  Admin
                </span>
              </span>
            </button>
            <div
              className="left-auto right-0 z-50 my-1 hidden list-none divide-y divide-gray-100 rounded border border-slate-700/10 text-base shadow dark:divide-gray-600 bg-white dark:bg-slate-800 w-40"
              id="navUserdata"
            >
              <ul className="py-1" aria-labelledby="user-profile">
                <li>
                  <a
                    href="#"
                    className="flex items-center py-2 px-3 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-900/20 dark:hover:text-white"
                  >
                    <span
                      data-lucide="user"
                      className="w-4 h-4 inline-block text-slate-800 dark:text-slate-400 me-2"
                    />
                    Profile
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="flex items-center py-2 px-3 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-900/20 dark:hover:text-white"
                  >
                    <span
                      data-lucide="settings"
                      className="w-4 h-4 inline-block text-slate-800 dark:text-slate-400 me-2"
                    />
                    Settings
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="flex items-center py-2 px-3 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-900/20 dark:hover:text-white"
                  >
                    <span
                      data-lucide="dollar-sign"
                      className="w-4 h-4 inline-block text-slate-800 dark:text-slate-400 me-2"
                    />
                    Earnings
                  </a>
                </li>
                <li>
                  <a
                    href="auth-lockscreen.html"
                    className="flex items-center py-2 px-3 text-sm text-red-500 hover:bg-gray-50 hover:text-red-600 dark:text-red-500 dark:hover:bg-gray-900/20 dark:hover:text-red-500"
                  >
                    <span
                      data-lucide="power"
                      className="w-4 h-4 inline-block text-red-500 dark:text-red-500 me-2"
                    />
                    Sign out
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}